import type { Command } from '@piccolohealth/ui';
import { ChakraV3 } from '@piccolohealth/ui';
import React from 'react';
import type { IconType } from 'react-icons';
import {
  FaCodepen,
  FaFont,
  FaGripLines,
  FaHeading,
  FaImage,
  FaListOl,
  FaListUl,
  FaMinus,
  FaQuoteLeft,
  FaTable,
} from 'react-icons/fa';
import { isExtensionEnabled, type TiptapCommandMenuState, type Variable } from '../../utils';
import { ImageUploadPage } from '../image/ImageUploadPage';

const getAction = (options: {
  kind: 'void' | 'goto';
  id: string;
  rawTitle: string;
  title: string;
  description: string;
  showTooltip?: boolean;
  category?: string;
  icon: IconType;
  action: (props: TiptapCommandMenuState) => void;
}) => {
  return {
    kind: options.kind,
    id: options.id,
    rawTitle: options.rawTitle,
    showTooltip: options.showTooltip,
    category: options.category,
    action: options.action,
    title: () => {
      return (
        <ChakraV3.HStack>
          <ChakraV3.Center
            flexShrink={0}
            h={10}
            w={10}
            p={2}
            borderWidth='1px'
            borderColor='gray.200'
            rounded='md'
            bg='white'
          >
            <ChakraV3.Icon>
              <options.icon />
            </ChakraV3.Icon>
          </ChakraV3.Center>
          <ChakraV3.Stack gap={0}>
            <ChakraV3.Text fontSize='sm'>{options.title}</ChakraV3.Text>
            <ChakraV3.Text fontSize='xs' color='secondary'>
              {options.description}
            </ChakraV3.Text>
          </ChakraV3.Stack>
        </ChakraV3.HStack>
      );
    },
  };
};

export const getEditingPages =
  (variables: Variable[]) =>
  (state: TiptapCommandMenuState): Command.Page<TiptapCommandMenuState>[] => {
    const pages: Command.Page<TiptapCommandMenuState>[] = [];

    const basePage: Command.ActionsPage<TiptapCommandMenuState> = {
      kind: 'actions',
      id: 'ROOT',
      rawTitle: 'What would you like to do?',
      title: <ChakraV3.Text>What would you like to do?</ChakraV3.Text>,
      searchable: true,
      actions: [
        getAction({
          kind: 'void',
          id: 'blockquote',
          rawTitle: 'Blockquote',
          title: 'Blockquote',
          description: 'Make writing standout',
          icon: FaQuoteLeft,
          action: (props) => props.editor.chain().focus().toggleBlockquote().run(),
        }),
        getAction({
          kind: 'void',
          id: 'text',
          rawTitle: 'Text',
          title: 'Text',
          description: 'Body sized text',
          icon: FaFont,
          action: (props) => props.editor.chain().focus().setParagraph().run(),
        }),
        getAction({
          kind: 'void',
          id: 'heading1',
          title: 'Heading 1',
          rawTitle: 'Heading 1',
          description: 'Big section heading',
          icon: FaHeading,
          action: (props) => props.editor.chain().focus().toggleHeading({ level: 1 }).run(),
        }),
        getAction({
          kind: 'void',
          id: 'heading2',
          title: 'Heading 2',
          description: 'Medium section heading',
          rawTitle: 'Heading 2',
          icon: FaHeading,
          action: (props) => props.editor.chain().focus().toggleHeading({ level: 2 }).run(),
        }),
        getAction({
          kind: 'void',
          id: 'heading3',
          title: 'Heading 3',
          rawTitle: 'Heading 3',
          description: 'Small section heading',
          icon: FaHeading,
          action: (props) => props.editor.chain().focus().toggleHeading({ level: 3 }).run(),
        }),
        getAction({
          kind: 'void',
          id: 'bulletedList',
          title: 'Bulleted list',
          rawTitle: 'Bulleted list',
          description: 'Create a list with bullets',
          icon: FaListUl,
          action: (props) => props.editor.chain().focus().toggleBulletList().run(),
        }),
        getAction({
          kind: 'void',
          id: 'orderedList',
          title: 'Numbered list',
          rawTitle: 'Numbered list',
          description: 'Create a list with numbering',
          icon: FaListOl,
          action: (props) => props.editor.chain().focus().toggleOrderedList().run(),
        }),
        getAction({
          kind: 'void',
          id: 'table',
          title: 'Table',
          rawTitle: 'Table',
          description: 'Add simple tabular content',
          icon: FaTable,
          action: (props) => props.editor.chain().focus().insertTable().run(),
        }),
        getAction({
          kind: 'void',
          id: 'horizontalRule',
          title: 'Divider',
          rawTitle: 'Divider',
          description: 'Visually divide content',
          icon: FaMinus,
          action: (props) => props.editor.chain().focus().setHorizontalRule().run(),
        }),
        getAction({
          kind: 'void',
          id: 'pageBreak',
          title: 'Page break',
          rawTitle: 'Page break',
          description: 'Start a new page when printing',
          icon: FaGripLines,
          action: (props) => props.editor.chain().focus().setPageBreak().run(),
        }),
      ],
    };

    pages.push(basePage);

    if (isExtensionEnabled(state.editor, 'variable')) {
      basePage.actions.push(
        getAction({
          kind: 'goto',
          id: 'fields',
          title: 'Fields',
          rawTitle: 'Fields',
          description: 'Insert a data field into the document',
          icon: FaCodepen,
          action: () => {},
        }),
      );

      pages.push({
        kind: 'actions',
        id: 'fields',
        rawTitle: 'Fields',
        title: <ChakraV3.Text>Fields</ChakraV3.Text>,
        searchable: true,
        actions: variables.map((variable) => {
          return getAction({
            kind: 'void',
            id: variable.id,
            showTooltip: false,
            title: variable.label,
            rawTitle: variable.label,
            category: variable.category,
            icon: FaCodepen,
            description: variable.value.toString(),
            action: (props: TiptapCommandMenuState) => {
              props.editor
                .chain()
                .focus()
                .insertVariableAt(
                  props.range,
                  variable.id,
                  variable.arrayPath,
                  variable.arrayLength,
                )
                .run();
            },
          }) as Command.VoidAction<TiptapCommandMenuState>;
        }),
      });
    }

    if (isExtensionEnabled(state.editor, 'image')) {
      basePage.actions.push(
        getAction({
          kind: 'goto',
          id: 'image',
          title: 'Image',
          rawTitle: 'Image',
          description: 'Add an image',
          icon: FaImage,
          action: () => {},
        }),
      );

      pages.push({
        kind: 'free',
        id: 'image',
        rawTitle: 'Add an image',
        title: <ChakraV3.Text>Add an image</ChakraV3.Text>,
        body: (context) => {
          const onImageUpload = context.ctx.editor.storage.image.onImageUpload;

          return (
            <ImageUploadPage
              editor={context.ctx.editor}
              onExit={context.onExit}
              onImageUpload={onImageUpload}
            />
          );
        },
      });
    }

    return pages;
  };
